import React from 'react';
import { Box, Flex, Grid, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Dow } from '@services/engineService';
import { DAYS } from '@utils/v2/availability';

interface StepperProps {
  label: string;
  min: number;
  max: number;
  onChange: (value: number) => void;
}

interface Props {
  label: string;
  color: string;
  selectedTextColor?: string;
  /** Sessions this row must place. `null` hides the count line (DAYS_ONLY). */
  count: number | null;
  openDays: number;
  isSelected: (dow: Dow) => boolean;
  onToggleDay: (dow: Dow) => void;
  stepper?: StepperProps;
  hint?: string;
  message?: string;
  hideDays?: boolean;
  children?: React.ReactNode;
}

const Stepper: React.FC<StepperProps & { value: number }> = ({ label, value, min, max, onChange }) => (
  <HStack gap={1} bg="gray.100" rounded="full" p={1} alignSelf={{ base: 'start', md: 'center' }} flexShrink={0}>
    <IconButton
      aria-label={`Moins de ${label}`}
      size="sm"
      variant="ghost"
      bg="white"
      rounded="full"
      shadow="xs"
      disabled={value <= min}
      onClick={() => onChange(Math.max(min, value - 1))}
    >
      <MinusIcon width={16} />
    </IconButton>
    <Text minW="12" textAlign="center" fontSize="sm" color="gray.500">
      <Text as="span" fontWeight="bold" fontSize="md" color="gray.900">
        {value}
      </Text>{' '}
      /sem
    </Text>
    <IconButton
      aria-label={`Plus de ${label}`}
      size="sm"
      variant="ghost"
      bg="white"
      rounded="full"
      shadow="xs"
      disabled={value >= max}
      onClick={() => onChange(Math.min(max, value + 1))}
    >
      <PlusIcon width={16} />
    </IconButton>
  </HStack>
);

export const SportAvailabilityCard: React.FC<Props> = ({
  label,
  color,
  selectedTextColor = 'gray.900',
  count,
  openDays,
  isSelected,
  onToggleDay,
  stepper,
  hint,
  message,
  hideDays,
  children,
}) => {
  const short = count !== null && openDays < count;

  return (
    <VStack align="stretch" gap={3} borderWidth={1} borderColor="gray.200" rounded="xl" p={4} bg="white">
      <Flex direction={{ base: 'column', md: 'row' }} justifyContent="space-between" gap={3}>
        <HStack align="start" gap={3}>
          <Box w={2} h={5} mt={0.5} bg={color} transform="skewX(-20deg)" rounded="xs" flexShrink={0} />
          <VStack align="start" gap={0}>
            <Text fontWeight="bold" color="gray.900">
              {label}
            </Text>
            {count !== null && (
              <Text fontSize="xs" color={short ? 'orange.600' : 'gray.500'} fontWeight={short ? 'bold' : 'normal'}>
                {count} séance{count > 1 ? 's' : ''} · {openDays} jour{openDays > 1 ? 's' : ''} ouvert
                {openDays > 1 ? 's' : ''}
              </Text>
            )}
            {hint && (
              <Text fontSize="xs" color="gray.500">
                {hint}
              </Text>
            )}
          </VStack>
        </HStack>
        {stepper && count !== null && <Stepper {...stepper} value={count} />}
      </Flex>

      {!hideDays && (
        <Grid templateColumns="repeat(7, 1fr)" gap={{ base: 1.5, md: 2 }}>
          {DAYS.map((day) => {
            const selected = isSelected(day.dow);
            return (
              <Box
                key={day.dow}
                as="button"
                h={{ base: 10, md: 11 }}
                rounded="md"
                borderWidth={1}
                borderColor={selected ? 'transparent' : 'gray.200'}
                bg={selected ? color : 'white'}
                color={selected ? selectedTextColor : 'gray.500'}
                fontWeight={selected ? 'bold' : 'medium'}
                fontSize="sm"
                transition="background 0.15s, border-color 0.15s"
                _hover={selected ? undefined : { borderColor: 'gray.300', bg: 'gray.50' }}
                aria-pressed={selected}
                onClick={() => onToggleDay(day.dow)}
              >
                {day.label}
              </Box>
            );
          })}
        </Grid>
      )}

      {children}

      {message && (
        <Text fontSize="xs" color="orange.600">
          {message}
        </Text>
      )}
    </VStack>
  );
};

export default SportAvailabilityCard;
